// author-liumingyang 2022-3-8  经分
import React, { useMemo } from 'react';
import { echartsContent } from '@/pages/opinion/utils';
import EChartCommon from '@/pages/opinion/components/EChartCommon';
import { formatChartParamsDate } from '@/pages/opinion/components/ChartCommon/formatter';
import { percentageFormat, reviewsFormat } from '@/pages/opinion/utils/value';
import { baseColors, extendColors } from '@/pages/opinion/constants';
import { useIntl, getLocale } from 'umi';
import { dealwithTitle } from '../../utils';

const colorArr = [...baseColors, ...extendColors];

export default function BarEchart({
  data,
  height = 300,
}: {
  data: any;
  height?: number;
}) {
  const { formatMessage } = useIntl();
  const locale = getLocale();

  // 设置图形配置
  const makeupOptions = () => {
    const legends: { name: string; itemStyle: { color: string } }[] = [];

    const seriesData = data.map(
      (item: { rate: number; count: number; key: string }, index: number) => {
        const color = colorArr[index];
        return {
          ...item,
          name: item.key,
          value: item.rate,
          itemStyle: {
            color,
            barBorderRadius: 2,
          },
        };
      },
    );

    const opts = {
      grid: { left: 12, right: '5%', bottom: 0 },
      color: colorArr,
      tooltip: {
        formatter: (params: any) => {
          return formatChartParamsDate({
            params,
            listTitleFunc: (params) => {
              return dealwithTitle(params.axisValue);
            },
            recordDealwith: (rows, ele) => {
              rows['share'] = percentageFormat(ele?.data?.rate * 100);
              rows['data'] = reviewsFormat(ele?.data?.count);
              return rows;
            },
            tablesConfigFunc: (records: Array<any>) => {
              return [
                {
                  showHeader: true,
                  records,
                  columns: [
                    {
                      key: 'text',
                      header: formatMessage({ id: 'Time' }),
                    },
                    {
                      key: 'data',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: 'Reviews' }),
                    },
                    {
                      key: 'share',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: 'Share-1' }),
                    },
                  ],
                },
              ];
            },
          });
        },
      },
      legend: {
        data: legends,
        formatter: (name: string) => {
          return dealwithTitle(name);
        },
      },
      yAxis: {
        position: 'left',
        axisLabel: {
          show: true,
          margin: 15,
          interval: 0,
          align: 'right',
          formatter: (value: string) => {
            return dealwithTitle(value);
          },
        },
        splitLine: {
          show: false,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
        axisLine: {
          show: false,
        },
        type: 'category',
        axisTick: {
          show: false,
        },
        gridIndex: 0,
        data: [...seriesData.map((item: { name: any }) => item.name)],
      },
      xAxis: {
        axisTick: {
          alignWithLabel: true,
          lineStyle: {
            color: '#000000',
            opacity: '0.25',
          },
        },
        boundaryGap: true,
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#E5E5E5' },
        },
        axisLabel: {
          interval: 'auto',
          formatter: (value: number) => {
            return Math.round(value * 100) + '%';
          },
        },
      },
      series: [
        {
          name: 'rate',
          barMaxWidth: 30,
          type: 'bar',
          data: seriesData,
          label: {
            show: true,
            position: 'right',
            fontSize: 12,
            color: '#444',
            formatter: (params: { data: { count: any } }) => {
              return echartsContent(params?.data?.count);
            },
          },
        },
      ],
    };
    return opts;
  };

  const options = useMemo(() => {
    if (data.length === 0) {
      return {};
    }
    return makeupOptions();
  }, [data, locale]);

  return (
    <EChartCommon
      optionsProps={{ type: 'bar' }}
      options={options}
      empty={data.length === 0}
      height={height}
    ></EChartCommon>
  );
}
